/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Page Tariffs And Services Constants
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.4.0
 */

import { IPageProductsTranslation } from '../../modules/internationalization/types/pages/products';
import { IProductsPaymentItem } from './tariffsAndServicesTypes';

export const MINIMAL_SEACRCH_STRING_LENGTH = 3;

export enum EPreselectedCategory {
    ROAMING_CATEGORY_ID = 'roamingCategoryId'
}

export enum EProductsPageFrom {
    CAMPAIGNS = 'CAMPAIGNS',
    HEADER = 'HEADER',
    SUBSCRIBER_ID = 'SUBSCRIBER_ID',
    SUBSCRIBER_ID_CHANGE_MEGA_POWERS = 'SUBSCRIBER_ID_CHANGE_MEGA_POWERS',
    SUBSCRIBERS = 'SUBSCRIBERS',
}

export enum EProductsPayment {
    ALL = 'ALL',
    NO_FEE = 'NO_FEE',
    WITH_FEE = 'WITH_FEE',
}

export enum EProductsCart {
    PUBLIC = 'PUBLIC',
    CAMPAIGN = 'CAMPAIGN',
}

export enum EProductsCategory {
    ROOT = -5,
    CAMPAIGN_SERVICES = -4,
    CAMPAIGN_TARIFFS = -3,
    SEARCH = -2,
    SELECTED = -1,
    POPULAR = 4, // default only
    SERVICES = 3, // default only
    TARIFFS = 2, // default only
    OTHER = -10, // default only
    MEGAPOWERS = -11, // default only
    ROAMING = -12, // default only
}

export enum EProductsCategoryUrl {
    SERVICES = 'services',
    TARIFFS = 'tariff',
    MEGAPOWERS = 'megapowers',
}

export const formProductsPaymentOptions: (translation: IPageProductsTranslation) => IProductsPaymentItem[] = (
    translation,
) => [
    {
        label: translation.filters.payment.all,
        value: EProductsPayment.ALL,
    },
    {
        label: translation.filters.payment.withFee,
        value: EProductsPayment.WITH_FEE,
    },
    {
        label: translation.filters.payment.noFee,
        value: EProductsPayment.NO_FEE,
    },
];

export enum EProductsTab {
    AVAILABLE = 'AVAILABLE',
    CONNECTED = 'CONNECTED',
}

export const PRODUCTS_INIT_ACTION = '@@b2b_portal_ui/products/PRODUCTS_INIT_ACTION';

export const PRODUCTS_GET_META_ACTION = '@@b2b_portal_ui/products/PRODUCTS_GET_META_ACTION';
export const PRODUCTS_GET_META_SUCCESS = '@@b2b_portal_ui/products/PRODUCTS_GET_META_SUCCESS';
export const PRODUCTS_GET_META_FAILURE = '@@b2b_portal_ui/products/PRODUCTS_GET_META_FAILURE';

export const PRODUCTS_GET_CATEGORY_LIST_ACTION = '@@b2b_portal_ui/products/PRODUCTS_GET_CATEGORY_LIST_ACTION';
export const PRODUCTS_GET_CATEGORY_LIST_SUCCESS = '@@b2b_portal_ui/products/PRODUCTS_GET_CATEGORY_LIST_SUCCESS';
export const PRODUCTS_GET_CATEGORY_LIST_FAILURE = '@@b2b_portal_ui/products/PRODUCTS_GET_CATEGORY_LIST_FAILURE';

export const PRODUCTS_GET_PRODUCT_LIST_ACTION = '@@b2b_portal_ui/products/PRODUCTS_GET_PRODUCT_LIST_ACTION';
export const PRODUCTS_GET_PRODUCT_LIST_SUCCESS = '@@b2b_portal_ui/products/PRODUCTS_GET_PRODUCT_LIST_SUCCESS';
export const PRODUCTS_GET_PRODUCT_LIST_AVAILABLE_FAILURE_ACTION =
    '@@b2b_portal_ui/products/PRODUCTS_GET_PRODUCT_LIST_AVAILABLE_FAILURE_ACTION';
export const PRODUCTS_GET_PRODUCT_LIST_CONNECTED_FAILURE_ACTION =
    '@@b2b_portal_ui/products/PRODUCTS_GET_PRODUCT_LIST_CONNECTED_FAILURE_ACTION';

export const GET_TARIFFS_AND_SERVICES_REGION_LIST_ACTION =
    '@@b2b_portal_ui/products/GET_TARIFFS_AND_SERVICES_REGION_LIST_ACTION';
export const GET_TARIFFS_AND_SERVICES_REGION_LIST_SUCCESS =
    '@@b2b_portal_ui/products/GET_TARIFFS_AND_SERVICES_REGION_LIST_SUCCESS';
export const GET_TARIFFS_AND_SERVICES_REGION_LIST_FAILURE =
    '@@b2b_portal_ui/products/GET_TARIFFS_AND_SERVICES_REGION_LIST_FAILURE';

export const SET_TARIFFS_AND_SERVICES_REGION_DISABLED_ACTION =
    '@@b2b_portal_ui/products/GET_TARIFFS_AND_SERVICES_REGION_DISABLED_ACTION';

export const PRODUCTS_SET_CATEGORY_SELECTED = '@@b2b_portal_ui/products/PRODUCTS_SET_CATEGORY_SELECTED';

export const TARIFFS_AND_SERVICES_SET_SEARCH_VALUE = '@@b2b_portal_ui/products/TARIFFS_AND_SERVICES_SET_SEARCH_VALUE';

export const TARIFFS_AND_SERVICES_SEARCH_START = '@@b2b_portal_ui/products/TARIFFS_AND_SERVICES_SEARCH_START';

export const SET_TARIFFS_AND_SERVICES_ITEM_SELECTED = '@@b2b_portal_ui/products/SET_TARIFFS_AND_SERVICES_ITEM_SELECTED';
export const SET_TARIFFS_AND_SERVICES_ITEMS_SELECTED =
    '@@b2b_portal_ui/products/SET_TARIFFS_AND_SERVICES_ITEMS_SELECTED';
export const SET_TARIFFS_AND_SERVICES_TARIFF_SELECTED =
    '@@b2b_portal_ui/products/SET_TARIFFS_AND_SERVICES_TARIFF_SELECTED';
export const SET_TARIFFS_AND_SERVICES_SET_TARIFF_RANGE =
    '@@b2b_portal_ui/products/SET_TARIFFS_AND_SERVICES_SET_TARIFF_RANGE';

export const SET_TARIFFS_AND_SERVICES_PAYMENT_SELECTED =
    '@@b2b_portal_ui/products/SET_TARIFFS_AND_SERVICES_PAYMENT_SELECTED';

export const SET_TARIFFS_AND_SERVICES_REGION_SELECTED =
    '@@b2b_portal_ui/products/SET_TARIFFS_AND_SERVICES_REGION_SELECTED';

export const SET_TARIFFS_AND_SERVICES_FILTER_REGION_VISIBLE =
    '@@b2b_portal_ui/products/SET_TARIFFS_AND_SERVICES_FILTER_REGION_VISIBLE';

export const PRODUCTS_SET_CATEGORY_IDS = '@@b2b_portal_ui/products/PRODUCTS_SET_CATEGORY_IDS';

export const PRODUCTS_SET_TAB_SELECTED = '@@b2b_portal_ui/products/PRODUCTS_SET_TAB_SELECTED';
export const PRODUCTS_SET_PAGE_FROM = '@@b2b_portal_ui/products/PRODUCTS_SET_PAGE_FROM';
export const PRODUCTS_SET_FILTER_SELECTION = '@@b2b_portal_ui/products/PRODUCTS_SET_FILTER_SELECTION';
export const PRODUCTS_CONNECT_SELECTED_SERVICES = '@@b2b_portal_ui/products/PRODUCTS_CONNECT_SELECTED_SERVICES';
export const PRODUCTS_CLEAR_ACTION = '@@b2b_portal_ui/products/PRODUCTS_CLEAR_ACTION';

export const PRODUCTS_START_WIZARD_FROM_SUBSCRIBER_ID =
    '@@b2b_portal_ui/products/PRODUCTS_START_WIZARD_FROM_SUBSCRIBER_ID';

export const GET_ROAMING_OPTIONS_COUNTRIES_SEARCH = '@@b2b_portal_ui/products/GET_ROAMING_OPTIONS_COUNTRIES_SEARCH';
export const GET_ROAMING_OPTIONS_COUNTRIES_SEARCH_SUCCESS = '@@b2b_portal_ui/products/GET_ROAMING_OPTIONS_COUNTRIES_SEARCH_SUCCESS';
export const GET_ROAMING_OPTIONS_COUNTRIES_SEARCH_FAILURE = '@@b2b_portal_ui/products/GET_ROAMING_OPTIONS_COUNTRIES_SEARCH_FAILURE';

export const GET_ROAMING_OPTIONS_FOR_COUNTRY = '@@b2b_portal_ui/products/GET_ROAMING_OPTIONS_FOR_COUNTRY';
export const GET_ROAMING_OPTIONS_FOR_COUNTRY_SUCCESS = '@@b2b_portal_ui/products/GET_ROAMING_OPTIONS_FOR_COUNTRY_SUCCESS';
export const GET_ROAMING_OPTIONS_FOR_COUNTRY_FAILURE = '@@b2b_portal_ui/products/GET_ROAMING_OPTIONS_FOR_COUNTRY_FAILURE';

export const SET_IS_ROAMING_OPTIONS_ERROR = '@@b2b_portal_ui/products/SET_IS_ROAMING_OPTIONS_ERROR';
export const SET_SELECTED_COUNTRY_CODE = '@@b2b_portal_ui/products/SET_SELECTED_COUNTRY_CODE';
export const SET_SELECTED_ROAMING_OPTION = '@@b2b_portal_ui/products/SET_SELECTED_ROAMING_OPTION';

export const CLEAR_ROAMING_OPTIONS_SEARCH_ACTION = '@@b2b_portal_ui/products/CLEAR_ROAMING_OPTIONS_SEARCH';

export const SET_INCLUDE_CAMPAIGN_TARIFF_PRODUCTS = '@@b2b_portal_ui/products/SET_INCLUDE_CAMPAIGN_TARIFF_PRODUCTS';
export const SET_INCLUDE_CAMPAIGN_SERVICE_PRODUCTS = '@@b2b_portal_ui/products/SET_INCLUDE_CAMPAIGN_SERVICE_PRODUCTS';
